import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  X, DownloadSimple, ShareNetwork,
  LockKey, Archive, Warning, ChatCircleText, ClockCounterClockwise, Info,
  Stamp, PencilSimple, FileText, Trash, CheckCircle, Seal, PaperPlaneTilt,
  ArrowUUpLeft, Prohibit, Scissors, Factory, Books, CaretLeft, CaretRight, Sparkle, PaperPlaneRight, LinkSimple,
  ArrowClockwise, ArrowCounterClockwise,
} from '@phosphor-icons/react';

// 関連レコードの遷移先。ラベルはAPIの target_type_label を優先する（日本語は単一ソース）。
const LINK_ROUTE = {
  project: (id) => `/planner/project/${id}`,
  daily_report: (id) => `/engineering/reports/${id}`,
  eng_report: (id) => `/engineering/reports/${id}`,
  eng_asset: (id) => `/engineering/assets?open=${id}`,
  it_asset: (id) => `/it/assets?open=${id}`,
  bihin_item: (id) => `/bihin/items?open=${id}`,
  kb_page: (id) => `/knowledge/p/${id}`,
  // item_flow / output_item は画面（旧アイテムフロー）を撤去したので遷移先を持たない。
  // ここに無い target_type はボタンが disabled になり、ラベルだけ残る。
  equipment: (id) => `/engineering/equipment?open=${id}`,
  rental: (id) => `/engineering/rentals?open=${id}`,
  mfg_item: (id) => `/mfg/inventory?open=${id}`,
  work_order: (id) => `/mfg/production?open=${id}`,
};
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import DocAskPanel from './DocAskPanel';
import DocReadPanel from './DocReadPanel';
import AddToBundleDialog from './AddToBundleDialog';
import ShareMenu from './ShareMenu';
import MixedDocNotice from './MixedDocNotice';
import { OfficePreview } from './OfficePreview';
import { ZoomPanImage } from './ZoomPanImage';
import { dmsApi, SIMPLE_STATUS_STYLE, fmtSize, fmtDate } from './dmsApi';
import { formatJSTShort } from '@/utils/datetime';

/** スタンプ定義 — ボタン → 監査付きバックエンドトランザクション */
const STAMP_BUTTONS = [
  { id: 'reviewed', label: '確認済', Icon: CheckCircle, perm: 'edit', cls: 'text-cyan-300 hover:bg-cyan-500/20' },
  { id: 'approved', label: '承認', Icon: Seal, perm: 'manage', cls: 'text-emerald-300 hover:bg-emerald-500/20' },
  { id: 'released', label: '発行', Icon: PaperPlaneTilt, perm: 'manage', cls: 'text-indigo-300 hover:bg-indigo-500/20' },
  { id: 'rejected', label: '差戻し', Icon: ArrowUUpLeft, perm: 'edit', cls: 'text-rose-300 hover:bg-rose-500/20', needsNote: true },
  { id: 'obsolete', label: '旧版', Icon: Prohibit, perm: 'manage', cls: 'text-amber-300 hover:bg-amber-500/20' },
  { id: 'confidential', label: '社外秘', Icon: LockKey, perm: 'manage', cls: 'text-rose-300 hover:bg-rose-500/20' },
  { id: 'needs_split', label: '要分割', Icon: Scissors, perm: 'edit', cls: 'text-orange-300 hover:bg-orange-500/20' },
  { id: 'for_manufacturing', label: '製造用', Icon: Factory, perm: 'edit', cls: 'text-sky-300 hover:bg-sky-500/20' },
];

export default function DocViewer({ docId, meta, onClose, onChanged, onOpenEditor, bundleNav,
                                   initialPage, onOpenDoc }) {
  const [askOpen, setAskOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [bundleAddOpen, setBundleAddOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  // 1 · 向き（ページごと）。PDFは表示中ページ、画像は1ページ目として扱う。
  const [rotPage, setRotPage] = useState(initialPage || 1);
  const [tab, setTab] = useState('info');       // info | comments | history | versions
  const [commentText, setCommentText] = useState('');
  const [commentPage, setCommentPage] = useState('');
  const [noteDlg, setNoteDlg] = useState(null); // {stamp,label}
  const [noteText, setNoteText] = useState('');
  const [ocr, setOcr] = useState(null);
  const [askMsgs, setAskMsgs] = useState([]);   // [{q, a}]
  const [askText, setAskText] = useState('');
  const [asking, setAsking] = useState(false);
  const navigate = useNavigate();

  const sendAsk = async () => {
    const q = askText.trim();
    if (!q || asking) return;
    setAsking(true);
    setAskText('');
    try {
      const r = await dmsApi.askDocument(docId, q);
      setAskMsgs((m) => [...m, { q, a: r.answer }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI応答の取得に失敗しました');
      setAskText(q);
    } finally { setAsking(false); }
  };

  useEffect(() => {
    if (!meta.features?.ocr_text_search) return;
    dmsApi.ocrInfo(docId).then(setOcr).catch(() => setOcr(null));
  }, [docId, meta.features?.ocr_text_search]);

  const [kwBusy, setKwBusy] = useState(false);
  const [aiTags, setAiTags] = useState(null);
  const rebuildKeywords = async () => {
    setKwBusy(true);
    try {
      const r = await dmsApi.rebuildKeywords(docId);
      toast.success(`検索キーワード ${r.keywords.length}件 を生成しました`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'キーワード生成に失敗しました'); }
    finally { setKwBusy(false); }
  };
  const genAiTags = async () => {
    setKwBusy(true);
    try {
      await dmsApi.aiTags(docId);
      setAiTags({ status: 'processing' });
      toast.info('AIタグの生成を開始しました — 終わると自動で表示されます');
    } catch (e) { toast.error(e?.response?.data?.detail || 'AIタグの生成を開始できませんでした'); }
    finally { setKwBusy(false); }
  };
  const urlRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await dmsApi.document(docId);
      setDetail(d);
      const doc = d.document;
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
      setPreviewUrl(null);
      const mime = doc.mime_type || '';
      if (doc.preview_allowed && meta.features?.preview_service &&
          (mime === 'application/pdf' || mime.startsWith('image/'))) {
        try {
          const { url } = await dmsApi.fileBlobUrl(docId);
          urlRef.current = url;
          // 出典ジャンプ: PDFは指定ページを開く（#page=N はブラウザのPDFビューアが解釈する）
          setPreviewUrl(initialPage && mime === 'application/pdf'
            ? `${url}#page=${initialPage}` : url);
        } catch { /* optional */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || '書類の取得に失敗しました');
      onClose?.();
    }
  }, [docId, meta, onClose, initialPage]);

  useEffect(() => {
    load();
    return () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); };
  }, [load]);

  // AIタグ: 状況を見て、処理中なら終わるまで見張る。
  // 生成本体はサーバー側で走っているので、画面を離れても止まらない。
  useEffect(() => {
    let alive = true;
    let timer = null;
    if (!meta.features?.ai_assist) return undefined;
    const tick = async () => {
      try {
        const s = await dmsApi.aiTagsStatus(docId);
        if (!alive) return;
        setAiTags(s);
        if (s.status === 'processing') {
          timer = setTimeout(tick, 4000);
        } else if (s.status === 'done') {
          load();
        }
      } catch { /* 状況が取れないだけなので画面は壊さない */ }
    };
    tick();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [docId, meta.features?.ai_assist, load]);

  // Esc で閉じる（全画面ビューアは他の操作を覆うため）
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const doc = detail?.document;
  if (!doc) return null;
  const canStamp = meta.features?.stamps_status_lifecycle && !doc.read_only && !doc.is_deleted;
  const rotations = doc.page_rotations || {};
  const deg = Number(rotations[String(rotPage)] || 0);

  const rotate = async (delta) => {
    try {
      await dmsApi.rotate(docId, rotPage, delta);
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '向きを保存できませんでした'); }
  };

  const printRotated = () => {
    if (!previewUrl) return;
    const w = window.open('', '_blank');
    if (!w) { toast.error('ポップアップを許可してください'); return; }
    const body = doc.mime_type === 'application/pdf'
      ? `<iframe src="${previewUrl}" style="width:100%;height:100vh;border:0;transform:rotate(${deg}deg)"></iframe>`
      : `<img src="${previewUrl}" style="max-width:100%;transform:rotate(${deg}deg)" />`;
    w.document.write(`<!doctype html><title>${doc.title}</title>`
      + `<body style="margin:0;display:flex;align-items:center;justify-content:center">${body}`
      + '<script>setTimeout(()=>window.print(),600)<\/script>');
    w.document.close();
  };

  const resetRotation = async () => {
    try {
      await dmsApi.resetRotation(docId);
      await load(); onChanged?.();
      toast.success('元の向きに戻しました');
    } catch (e) { toast.error(e?.response?.data?.detail || '戻せませんでした'); }
  };

  const applyStamp = async (stamp, note) => {
    try {
      const r = await dmsApi.stamp(docId, stamp, note);
      toast.success(`「${r.label}」スタンプを適用しました`);
      setNoteDlg(null); setNoteText('');
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'スタンプに失敗しました'); }
  };

  const submitComment = async () => {
    if (!commentText.trim()) return;
    try {
      await dmsApi.addComment(docId, commentText.trim(), commentPage ? Number(commentPage) : null);
      setCommentText(''); setCommentPage('');
      toast.success('コメントを追加しました');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'コメントに失敗しました'); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-900/95 backdrop-blur-sm flex flex-col" role="dialog" aria-modal="true" data-testid="dms-viewer">
      {/* Header */}
      <header className="h-14 shrink-0 flex items-center justify-between gap-3 px-4 border-b border-zinc-700/60">
        <div className="flex items-center gap-3 min-w-0">
          <FileText size={20} weight="duotone" className="text-teal-400 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate" title={doc.title} data-testid="dms-viewer-title">{doc.title}</p>
            <p className="text-[10px] text-zinc-400 font-mono">{doc.document_number} · v{detail.versions?.[0]?.version_number || 1} · {doc.owner_name}</p>
          </div>
          <span className={`px-2 py-0.5 rounded border text-[11px] font-bold shrink-0 ${SIMPLE_STATUS_STYLE[doc.status_simple] || ''}`}
            data-testid="dms-viewer-status">{doc.status_simple_label}</span>
          {doc.is_archived && (
            <span className="px-2 py-0.5 rounded bg-stone-700 text-stone-200 text-[10px] inline-flex items-center gap-1 shrink-0">
              <Archive size={10} weight="fill" /> アーカイブ
            </span>
          )}
          {['restricted', 'confidential'].includes(doc.confidentiality) && (
            <span className="px-2 py-0.5 rounded bg-rose-900/60 text-rose-200 text-[10px] inline-flex items-center gap-1 shrink-0">
              <LockKey size={10} weight="fill" /> {doc.confidentiality === 'confidential' ? '社外秘' : '制限付き'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {bundleNav && (
            <div className="flex items-center gap-1 bg-zinc-800/80 rounded-lg px-2 py-1" data-testid="dms-viewer-bundle-nav">
              <Books size={13} weight="duotone" className="text-amber-300" />
              <span className="text-[10px] text-amber-200 max-w-[120px] truncate hidden sm:inline">{bundleNav.label}</span>
              <button type="button" disabled={bundleNav.index <= 0} onClick={bundleNav.onPrev}
                aria-label="前の書類" title="前の書類"
                className="inline-flex items-center justify-center h-8 w-8 rounded text-zinc-300 hover:text-white disabled:opacity-30 focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:outline-none" data-testid="dms-bundle-prev">
                <CaretLeft size={15} weight="bold" />
              </button>
              <span className="text-[10px] text-zinc-300 tabular-nums">{bundleNav.index + 1}/{bundleNav.total}</span>
              <button type="button" disabled={bundleNav.index >= bundleNav.total - 1} onClick={bundleNav.onNext}
                aria-label="次の書類" title="次の書類"
                className="inline-flex items-center justify-center h-8 w-8 rounded text-zinc-300 hover:text-white disabled:opacity-30 focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:outline-none" data-testid="dms-bundle-next">
                <CaretRight size={15} weight="bold" />
              </button>
            </div>
          )}
          {/* U2 — 聞く入口は右側の「質問」タブだけにする（同じ機能のボタンを横に並べない） */}
          <button type="button" onClick={() => { setTab('ask'); setAskOpen(false); }}
            aria-label="質問タブを開く" title="質問タブを開く"
            data-testid="dms-viewer-ask-btn"
            className="inline-flex items-center gap-1.5 h-9 rounded-lg border border-zinc-600 px-3 text-xs text-zinc-200 hover:bg-zinc-800 hover:text-white focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:outline-none transition-colors">
            <ChatCircleText size={15} weight="duotone" /> 質問
          </button>
          <button type="button" onClick={onClose} aria-label="ビューアーを閉じる" className="text-zinc-400 hover:text-white p-2" data-testid="dms-viewer-close">
            <X size={20} weight="bold" />
          </button>
        </div>
      </header>

      {doc.status === 'obsolete' && (
        <div className="bg-amber-500/20 text-amber-200 text-xs px-4 py-1.5 flex items-center gap-1.5" data-testid="dms-viewer-obsolete-warning">
          <Warning size={13} weight="fill" /> この書類は旧版です — 最新版を確認してください
        </div>
      )}

      <div className="px-3 pt-3 empty:hidden" data-testid="dms-viewer-mixed-slot">
        <MixedDocNotice documentId={docId} />
      </div>

      <div className="flex-1 flex min-h-0">
        {/* Left rail: versions */}
        <aside className="w-40 shrink-0 border-r border-zinc-700/60 overflow-y-auto p-2 space-y-1.5 hidden md:block"
          data-testid="dms-viewer-rail">
          <p className="text-[10px] font-bold text-zinc-500 uppercase px-1">版</p>
          {(detail.versions || []).map((v) => (
            <div key={v.id} className={`rounded-lg border p-2 text-[10px] ${v.is_current ? 'border-teal-500/60 bg-teal-500/10 text-teal-200' : 'border-zinc-700 text-zinc-400'}`}>
              <p className="font-bold font-mono">v{v.version_number}{v.is_current ? '（現行）' : ''}</p>
              <p className="truncate">{v.filename}</p>
              <p className="text-zinc-500">{fmtDate(v.uploaded_at)}</p>
              <button type="button" className="mt-1 text-teal-400 hover:text-teal-200 inline-flex items-center gap-0.5"
                onClick={() => dmsApi.downloadFile(docId, v.id, v.filename)}>
                <DownloadSimple size={10} /> DL
              </button>
            </div>
          ))}
        </aside>

        {askOpen && (
          <div className="order-last fixed inset-0 z-40 bg-white md:static md:inset-auto md:z-auto md:bg-transparent">
            <DocAskPanel documentId={docId} folderId={doc.folder_id}
              documentTitle={doc.title} onClose={() => setAskOpen(false)}
              onOpenDoc={(id) => { setAskOpen(false); onOpenDoc?.(id); }} />
          </div>
        )}

        {/* Centre: preview */}
        <main className="flex-1 min-w-0 flex flex-col">
          <div className="h-9 shrink-0 flex items-center justify-center gap-1 border-b border-zinc-700/40">
            {/* 1 · 向きを変えて覚えさせる（原本は書き換えない・2026-11-05） */}
            {doc.preview_allowed && (
              <>
                <button type="button" onClick={() => rotate(-90)}
                  className="text-zinc-400 hover:text-white p-1.5 min-h-[32px] inline-flex items-center gap-1 text-[11px]"
                  data-testid="dms-viewer-rotate-left"><ArrowCounterClockwise size={14} /> 左に90°</button>
                <button type="button" onClick={() => rotate(90)}
                  className="text-zinc-400 hover:text-white p-1.5 min-h-[32px] inline-flex items-center gap-1 text-[11px]"
                  data-testid="dms-viewer-rotate-right"><ArrowClockwise size={14} /> 右に90°</button>
                {Object.keys(rotations).length > 0 && (
                  <button type="button" onClick={resetRotation}
                    className="text-amber-300 hover:text-white p-1.5 min-h-[32px] inline-flex items-center gap-1 text-[11px]"
                    data-testid="dms-viewer-rotate-reset">元の向きに戻す</button>
                )}
                {previewUrl && (
                  <button type="button" onClick={printRotated}
                    className="text-zinc-400 hover:text-white p-1.5 min-h-[32px] inline-flex items-center gap-1 text-[11px]"
                    data-testid="dms-viewer-print">この向きで印刷</button>
                )}
                {doc.mime_type === 'application/pdf' && (
                  <span className="text-[10px] text-zinc-500" data-testid="dms-viewer-rotate-page">
                    p.{rotPage}
                  </span>
                )}
              </>
            )}
            {doc.preview_allowed && (
              <button type="button" onClick={() => dmsApi.downloadFile(docId, null, doc.current_filename)}
                className="text-zinc-400 hover:text-white p-1.5 min-h-[32px] inline-flex items-center gap-1 text-[11px]"
                data-testid="dms-viewer-download"><DownloadSimple size={14} /> ダウンロード</button>
            )}
            <button type="button" onClick={() => setShareOpen(true)}
              className="text-zinc-400 hover:text-white p-1.5 min-h-[32px] inline-flex items-center gap-1 text-[11px]"
              data-testid="dms-viewer-share"><ShareNetwork size={14} /> 共有</button>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden flex items-center justify-center p-3" data-testid="dms-viewer-canvas">
            {!doc.preview_allowed ? (
              <div className="text-center text-zinc-500">
                <LockKey size={44} weight="duotone" className="mx-auto mb-2" />
                <p className="text-xs">保護された書類のためプレビューできません</p>
              </div>
            ) : previewUrl && doc.mime_type === 'application/pdf' ? (
              <iframe title="preview" src={previewUrl} className="w-full h-full bg-white rounded"
                style={deg ? { transform: `rotate(${deg}deg)` } : undefined}
                data-testid="dms-viewer-pdf" />
            ) : previewUrl ? (
              <div className="flex h-full w-full items-center justify-center"
                style={deg ? { transform: `rotate(${deg}deg)` } : undefined}
                data-testid="dms-viewer-rotor">
                <ZoomPanImage src={previewUrl} alt={doc.title} testId="dms-viewer-image" />
              </div>
            ) : (
              <OfficePreview docId={docId} doc={doc} dark heightClass="h-full" />
            )}
          </div>
        </main>

        {/* Right panel */}
        <aside className="w-72 shrink-0 border-l border-zinc-700/60 flex flex-col" data-testid="dms-viewer-panel">
          <div className="flex border-b border-zinc-700/40">
            {[['info', Info, '情報'], ['comments', ChatCircleText, 'コメント'],
              ['history', ClockCounterClockwise, '履歴'],
              ...(meta.features?.ai_assist && ocr?.status === 'done' ? [['ask', Sparkle, '質問']] : []),
            ].map(([k, IconC, label]) => (
              <button key={k} type="button" onClick={() => setTab(k)}
                data-testid={`dms-viewer-tab-${k}`}
                className={`flex-1 py-2 text-[10px] font-semibold inline-flex items-center justify-center gap-1 ${
                  tab === k ? 'text-teal-300 border-b-2 border-teal-400' : 'text-zinc-500 hover:text-zinc-300'}`}>
                <IconC size={12} /> {label}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto p-3 text-xs text-zinc-300 space-y-2">
            {tab === 'info' && (
              <div className="space-y-1.5" data-testid="dms-viewer-info">
                {[['書類番号', doc.document_number], ['種別', doc.doc_type_label], ['状態', doc.status_simple_label],
                  ['所有者', doc.owner_name], ['サイズ', fmtSize(doc.file_size)],
                  ['更新', fmtDate(doc.updated_at)], ['タグ', (doc.tags || []).join(', ') || '—'],
                  ['説明', doc.description || '—'],
                  ...(doc.approved_by ? [['承認者', `${doc.approved_by}（${fmtDate(doc.approved_at)}）`]] : []),
                  ...(doc.released_by ? [['発行者', `${doc.released_by}（${fmtDate(doc.released_at)}）`]] : []),
                ].map(([k, v]) => (
                  <div key={k} className="flex gap-2">
                    <span className="w-16 shrink-0 text-zinc-500">{k}</span>
                    <span className="min-w-0 break-words">{v}</span>
                  </div>
                ))}
                {doc.version_locked && (
                  <p className="text-[10px] text-indigo-300 bg-indigo-500/10 border border-indigo-500/30 rounded px-2 py-1"
                    data-testid="dms-viewer-locked">発行済 — 現行版はロックされています</p>
                )}
                {meta.can_edit && !doc.read_only && !doc.is_deleted && (
                  <Button size="sm" variant="outline" onClick={() => onOpenEditor?.(docId)}
                    className="h-8 text-[11px] mt-1 bg-transparent border-zinc-600 text-zinc-200 hover:bg-zinc-700"
                    data-testid="dms-viewer-edit-btn">
                    <PencilSimple size={12} className="mr-1" /> 詳細編集
                  </Button>
                )}
                {meta.can_edit && !doc.is_deleted && meta.features?.bundles_binders_envelopes && (
                  <Button size="sm" variant="outline" onClick={() => setBundleAddOpen(true)}
                    className="h-8 text-[11px] mt-1 ml-1.5 bg-transparent border-amber-600/60 text-amber-200 hover:bg-amber-600/20"
                    data-testid="dms-viewer-add-bundle-btn">
                    <Books size={12} className="mr-1" /> バインダーに追加
                  </Button>
                )}
                {meta.features?.ocr_text_search && ocr && (
                  <div className="mt-2 pt-2 border-t border-zinc-700/60" data-testid="dms-viewer-ocr">
                    {ocr.status === 'done' && (
                      <>
                        <p className="text-[10px] text-teal-300" data-testid="dms-viewer-ocr-done">
                          認識済（{ocr.pages}{/\.(xlsx|xlsm|xls|ods|csv)$/i.test(doc?.current_filename || '') ? 'シート' : 'ページ'}・{ocr.text_length.toLocaleString()}文字）— 検索で本文も一致します
                        </p>
                        {(ocr.partial_sheets?.length > 0 || ocr.skipped_sheets?.length > 0) && (
                          <div className="text-[10px] text-amber-300 mt-1 space-y-0.5"
                            data-testid="dms-viewer-ocr-partial">
                            {ocr.partial_sheets?.length > 0 && (
                              <p>※ 途中までしか読めていないシート（{ocr.partial_sheets.join('・')}）—
                                1000行×200列を超える分は検索に出てきません</p>
                            )}
                            {ocr.skipped_sheets?.length > 0 && (
                              <p>※ 読み取れなかったシート（{ocr.skipped_sheets.join('・')}）—
                                前のシートで読み取り時間の上限に達したためです</p>
                            )}
                          </div>
                        )}
                      </>
                    )}
                    {ocr.status === 'processing' && (
                      <p className="text-[10px] text-amber-300" data-testid="dms-viewer-ocr-processing">認識中…</p>
                    )}
                    {ocr.status === 'failed' && (
                      <p className="text-[10px] text-rose-300" data-testid="dms-viewer-ocr-failed">前回の読み取りに失敗しました</p>
                    )}
                    {/* L8/L9 — 再読取・本文とキーワードの手直し（履歴から戻せる） */}
                    <div className="mt-2">
                      <DocReadPanel documentId={docId} dark
                        canEdit={!!meta.can_edit && !doc.is_deleted && !doc.read_only}
                        onChanged={() => { load(); onChanged?.(); dmsApi.ocrInfo(docId).then(setOcr).catch(() => {}); }} />
                    </div>
                    <div className="mt-2" data-testid="dms-viewer-keywords">
                      {(doc.ai_tags || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5" data-testid="dms-viewer-ai-tags">
                          {doc.ai_tags.map((k) => (
                            <span key={k} className="text-[9px] text-violet-200 bg-violet-900/60 border border-violet-700 rounded px-1.5 py-0.5">{k}</span>
                          ))}
                        </div>
                      )}
                      {meta.can_edit && !doc.is_deleted && (
                        <div className="flex items-center gap-1.5 mt-2">
                          <Button size="sm" variant="outline" onClick={rebuildKeywords} disabled={kwBusy}
                            className="h-6 text-[10px] bg-transparent border-zinc-600 text-zinc-200 hover:bg-zinc-700"
                            data-testid="dms-viewer-keywords-btn">キーワード再生成</Button>
                          {meta.features?.ai_assist && (
                            <Button size="sm" variant="outline" onClick={genAiTags}
                              disabled={kwBusy || aiTags?.status === 'processing'}
                              className="h-6 text-[10px] bg-transparent border-violet-600 text-violet-200 hover:bg-violet-900/50"
                              data-testid="dms-viewer-ai-tags-btn">
                              {aiTags?.status === 'processing' ? '生成中…' : 'AIタグを生成'}
                            </Button>
                          )}
                        </div>
                      )}
                      {aiTags?.status === 'processing' && (
                        <p className="text-[10px] text-amber-300 mt-1" data-testid="dms-viewer-ai-tags-processing">
                          AIが読み取っています… この画面を離れても処理は続きます
                        </p>
                      )}
                      {aiTags?.status === 'failed' && (
                        <div className="mt-1 rounded border border-rose-500/40 bg-rose-950/40 px-2 py-1.5 space-y-0.5"
                          data-testid="dms-viewer-ai-tags-failed">
                          <p className="text-[10px] text-rose-300">{aiTags.error || 'AIタグを生成できませんでした'}</p>
                          {aiTags.failure && (
                            <div className="text-[10px] text-rose-400/90 space-y-0.5">
                              <p>原因: {aiTags.failure.error_type_label}
                                {aiTags.failure.http_status ? `（HTTP ${aiTags.failure.http_status}）` : ''}
                                {aiTags.failure.stage ? ` · ${aiTags.failure.stage} の段階` : ''}</p>
                              {(aiTags.failure.provider_label || aiTags.failure.model) && (
                                <p>使ったAI: {aiTags.failure.provider_label || '—'} / {aiTags.failure.model || '—'}</p>
                              )}
                              {aiTags.failure.detail && (
                                <p className="font-mono break-all text-[9px] text-rose-500">{aiTags.failure.detail}</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {((detail.links || []).length > 0 || (detail.bundles || []).length > 0) && (
                  <div className="mt-2 pt-2 border-t border-zinc-700/60" data-testid="dms-viewer-usage">
                    <p className="text-[10px] font-bold text-zinc-500 mb-1 flex items-center gap-1"><LinkSimple size={11} /> 利用箇所</p>
                    <div className="space-y-1">
                      {(detail.links || []).map((l) => {
                        const route = LINK_ROUTE[l.target_type];
                        return (
                          <button key={l.id} type="button"
                            onClick={() => route && navigate(route(l.target_id))}
                            disabled={!route}
                            className="w-full flex items-center gap-1.5 text-left rounded bg-zinc-800/60 px-2 py-1 hover:bg-zinc-700/60 transition-colors disabled:hover:bg-zinc-800/60"
                            data-testid={`dms-usage-link-${l.id}`}>
                            <span className="text-[8px] font-bold text-teal-300 bg-teal-900/40 border border-teal-800 rounded px-1 py-0.5 shrink-0">{l.target_type_label || l.target_type}</span>
                            <span className="text-[10px] text-zinc-300 truncate">{l.target_label || '—'}</span>
                          </button>
                        );
                      })}
                      {(detail.bundles || []).map((b) => (
                        <button key={b.id} type="button"
                          onClick={() => navigate(`/documents?bundle=${b.id}`)}
                          className="w-full flex items-center gap-1.5 text-left rounded bg-zinc-800/60 px-2 py-1 hover:bg-zinc-700/60 transition-colors"
                          data-testid={`dms-usage-bundle-${b.id}`}>
                          <span className="text-[8px] font-bold text-amber-300 bg-amber-900/40 border border-amber-800 rounded px-1 py-0.5 shrink-0 inline-flex items-center gap-0.5"><Books size={9} /> バインダー</span>
                          <span className="text-[10px] text-zinc-300 truncate">{b.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {tab === 'comments' && (
              <div className="space-y-2" data-testid="dms-viewer-comments">
                {meta.can_edit && meta.features?.annotations_pencilcase && !doc.is_deleted && (
                  <div className="space-y-1.5">
                    <Input value={commentText} onChange={(e) => setCommentText(e.target.value)}
                      placeholder="コメント・付箋を追加..." maxLength={2000}
                      className="h-8 text-xs bg-zinc-800 border-zinc-600 text-zinc-100"
                      data-testid="dms-comment-input" />
                    <div className="flex items-center gap-1.5">
                      <Input value={commentPage} onChange={(e) => setCommentPage(e.target.value)}
                        placeholder="頁" type="number" min="1"
                        className="h-7 w-14 text-[10px] bg-zinc-800 border-zinc-600 text-zinc-100"
                        data-testid="dms-comment-page" />
                      <Button size="sm" onClick={submitComment} className="h-7 text-[10px] bg-teal-700 hover:bg-teal-800"
                        data-testid="dms-comment-submit">追加</Button>
                    </div>
                  </div>
                )}
                {(detail.comments || []).map((c) => (
                  <div key={c.id} className={`rounded-lg p-2 border ${c.stamp ? 'border-amber-500/40 bg-amber-500/10' : 'border-zinc-700 bg-zinc-800/60'}`}
                    data-testid={`dms-comment-${c.id}`}>
                    <p className="text-[10px] text-zinc-500 flex items-center justify-between">
                      <span>{c.author_name}{c.page_no ? ` · p.${c.page_no}` : ''}{c.stamp ? ' · スタンプメモ' : ''}</span>
                      <span>{fmtDate(c.created_at)}</span>
                    </p>
                    <p className="text-zinc-200 mt-0.5 break-words">{c.body}</p>
                  </div>
                ))}
                {(detail.comments || []).length === 0 && <p className="text-zinc-600 text-center py-4">コメントはありません</p>}
              </div>
            )}
            {tab === 'history' && (
              <div className="space-y-1" data-testid="dms-viewer-history">
                {(detail.activity || []).map((a) => (
                  <p key={a.id} className="text-[10px] text-zinc-400 py-0.5 border-b border-zinc-800 last:border-0">
                    <span className="font-mono text-zinc-600">{formatJSTShort(a.at)}</span>{' '}
                    <span className="text-zinc-300">{a.actor_name}</span>{' '}
                    {a.action === 'stamp' ? `スタンプ「${a.detail?.label}」`
                      : a.action === 'rotated' ? `向きを保存（p.${a.detail?.page} ${a.detail?.degrees}°）`
                        : a.action === 'rotation_reset' ? '元の向きに戻した'
                          : a.action}
                  </p>
                ))}
              </div>
            )}
            {tab === 'ask' && (
              <div className="flex flex-col gap-2" data-testid="dms-viewer-ask">
                <p className="text-[10px] text-zinc-500">書類から読み取った本文を根拠にAIが回答します</p>
                <div className="space-y-2">
                  {askMsgs.length === 0 && !asking && (
                    <p className="text-[10px] text-zinc-600 bg-zinc-800/60 rounded p-2">例:「この請求書の支払期限は？」「合計金額はいくら？」</p>
                  )}
                  {askMsgs.map((m, i) => (
                    <div key={i} className="space-y-1">
                      <p className="text-[11px] text-teal-200 bg-teal-900/30 border border-teal-800/50 rounded-lg px-2 py-1.5" data-testid={`dms-ask-q-${i}`}>{m.q}</p>
                      <p className="text-[11px] text-zinc-200 bg-zinc-800/70 rounded-lg px-2 py-1.5 whitespace-pre-wrap" data-testid={`dms-ask-a-${i}`}>{m.a}</p>
                    </div>
                  ))}
                  {asking && <p className="text-[10px] text-amber-300 animate-pulse">回答を生成中…</p>}
                </div>
                <div className="flex items-center gap-1.5 mt-1">
                  <input value={askText} onChange={(e) => setAskText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && sendAsk()}
                    placeholder="書類に質問…"
                    className="flex-1 h-8 rounded-lg bg-zinc-800 border border-zinc-700 text-[11px] text-zinc-200 px-2 placeholder:text-zinc-600 focus:outline-none focus:border-teal-600"
                    data-testid="dms-ask-input" />
                  <button type="button" onClick={sendAsk} disabled={asking || !askText.trim()}
                    aria-label="質問する" title="質問する"
                    className="h-8 w-8 rounded-lg bg-teal-600 hover:bg-teal-500 disabled:opacity-40 flex items-center justify-center text-white shrink-0"
                    data-testid="dms-ask-send">
                    <PaperPlaneRight size={13} weight="fill" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Bottom task toolbar */}
      {meta.features?.task_toolbar && canStamp && (meta.can_edit || meta.can_manage) && (
        <footer className="h-14 shrink-0 border-t border-zinc-700/60 flex items-center justify-center gap-1 px-3 overflow-x-auto"
          data-testid="dms-task-toolbar">
          <Stamp size={16} className="text-zinc-500 mr-1 shrink-0" />
          {STAMP_BUTTONS.filter((s) => (s.perm === 'manage' ? meta.can_manage : meta.can_edit)).map((s) => (
            <button key={s.id} type="button"
              onClick={() => (s.needsNote ? setNoteDlg({ stamp: s.id, label: s.label }) : applyStamp(s.id))}
              data-testid={`dms-stamp-${s.id}`}
              className={`shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-semibold inline-flex items-center gap-1 transition-colors ${s.cls}`}>
              <s.Icon size={14} weight="duotone" /> {s.label}
            </button>
          ))}
        </footer>
      )}

      {/* Note dialog for rejected */}
      {noteDlg && (
        <div className="absolute inset-0 z-10 bg-black/60 flex items-center justify-center" data-testid="dms-stamp-note-dialog">
          <div className="bg-zinc-800 border border-zinc-600 rounded-xl p-4 w-96 space-y-3">
            <p className="text-sm font-semibold text-white">「{noteDlg.label}」— 理由メモ（必須）</p>
            <Input value={noteText} onChange={(e) => setNoteText(e.target.value)} maxLength={1000}
              placeholder="差戻し理由を入力..." className="h-9 text-xs bg-zinc-900 border-zinc-600 text-zinc-100"
              data-testid="dms-stamp-note-input" />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => { setNoteDlg(null); setNoteText(''); }}
                className="h-8 text-xs bg-transparent border-zinc-600 text-zinc-300" data-testid="dms-stamp-note-cancel">キャンセル</Button>
              <Button size="sm" onClick={() => { if (!noteText.trim()) { toast.error('理由メモを入力してください'); return; } applyStamp(noteDlg.stamp, noteText.trim()); }}
                className="h-8 text-xs bg-rose-600 hover:bg-rose-700" data-testid="dms-stamp-note-submit">適用</Button>
            </div>
          </div>
        </div>
      )}
      <ShareMenu open={shareOpen} onClose={() => setShareOpen(false)}
        documentIds={[docId]} isAdmin={!!meta?.is_admin} />
      <AddToBundleDialog open={bundleAddOpen} documentId={docId}
        onClose={() => setBundleAddOpen(false)}
        onDone={() => { setBundleAddOpen(false); load(); onChanged?.(); }} />
    </div>
  );
}
